import { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Puzzle, Code2, Copy, Download, ShieldCheck } from "lucide-react";

export function PluginTab() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/plugin/info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const fetchFile = async (path, filename) => {
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error("Download failed");
    const url = window.URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };
  const report = async (path, filename) => {
    try { await fetchFile(path, filename); toast.success("Report downloaded"); }
    catch { toast.error("Download failed"); }
  };

  const download = async () => {
    setBusy(true);
    try { await fetchFile("/admin/plugin/download", `myelite-favorites-${info?.version || "1.0.0"}.zip`); toast.success("Plugin downloaded"); }
    catch { toast.error("Download failed"); }
    finally { setBusy(false); }
  };

  const shortcode = '[myelite_favorite id="sch-2680"]';

  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="admin-plugin">
      <div className="md:col-span-2 bg-white rounded-3xl border border-border shadow-card p-7">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 rounded-xl bg-orange/15 flex items-center justify-center"><Puzzle className="h-6 w-6 text-orange" /></div>
          <div>
            <h2 className="font-display font-extrabold italic text-navy text-lg">Elite Favorites</h2>
            <p className="text-xs text-muted-foreground">Version {info?.version || "…"} · WordPress plugin</p>
          </div>
        </div>
        <ol className="space-y-3 text-sm text-navy list-decimal pl-5 mb-6">
          <li>Download the ZIP and upload it in WordPress → Plugins → Add New → Upload.</li>
          <li>Activate it, then open <b>Settings → Elite Favorites</b>.</li>
          <li>Set the API base and app base URLs to this Elite domain.</li>
          <li>Place a button with the shortcode below (id = sch-&lt;WordPress ID&gt; or prg-&lt;WordPress ID&gt;).</li>
        </ol>
        <div className="flex items-center gap-2 bg-navy text-white rounded-xl px-4 py-3 font-mono text-sm">
          <Code2 className="h-4 w-4 text-orange shrink-0" />
          <span className="flex-1 truncate">{shortcode}</span>
          <button data-testid="copy-shortcode" onClick={() => { navigator.clipboard.writeText(shortcode); toast.success("Copied"); }} className="text-white/70 hover:text-white"><Copy className="h-4 w-4" /></button>
        </div>
        <Button data-testid="download-plugin-btn" onClick={download} disabled={busy} className="mt-6 h-12 rounded-full bg-orange hover:bg-orange-dark text-white font-display font-bold italic uppercase tracking-wide">
          <Download className="h-4 w-4 mr-2" /> {busy ? "Preparing…" : "Download plugin (.zip)"}
        </Button>
      </div>
      <div className="bg-navy text-white rounded-3xl p-7">
        <ShieldCheck className="h-8 w-8 text-orange mb-3" />
        <h3 className="font-display font-extrabold italic text-lg">Secure by design</h3>
        <ul className="mt-3 space-y-2 text-sm text-white/70">
          <li>• Bearer-token auth — no anonymous writes</li>
          <li>• Item data resolved server-side from the catalog</li>
          <li>• Per-user rate limiting & favorite caps</li>
          <li>• Scoped 24h widget token, bound to whitelisted origins, revoked on logout</li>
        </ul>
        <div className="mt-6 pt-5 border-t border-white/15">
          <p className="text-xs text-white/60 mb-2">API security report (FR)</p>
          <div className="flex gap-2">
            <Button size="sm" data-testid="security-report-pdf" onClick={() => report("/admin/security-report?format=pdf", "elite-securite-api.pdf")} className="rounded-full bg-orange hover:bg-orange-dark text-white"><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
            <Button size="sm" data-testid="security-report-docx" onClick={() => report("/admin/security-report?format=docx", "elite-securite-api.docx")} variant="outline" className="rounded-full bg-transparent border-white/30 text-white hover:bg-white/10"><Download className="h-3.5 w-3.5 mr-1" /> Word</Button>
          </div>
          <p className="text-xs text-white/60 mt-4 mb-2">Code architecture (FR)</p>
          <Button size="sm" data-testid="architecture-report-pdf" onClick={() => report("/admin/architecture-report", "elite-architecture-code.pdf")} className="rounded-full bg-orange hover:bg-orange-dark text-white"><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
        </div>
      </div>
    </div>
  );
}
